// stores/auth.js
import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import apiClient, { setAuthToken } from '@/api'

export const useUserStore = defineStore('user', () => {
  const accessToken = ref(localStorage.getItem("accessToken"))
  const refreshToken = ref(localStorage.getItem("refreshToken"))
  const current_user = ref(localStorage.getItem("current_user") ? JSON.parse(localStorage.getItem("current_user")) : null)
  const backend_groups = ref(localStorage.getItem("backend_groups") ? JSON.parse(localStorage.getItem("backend_groups")) : {})
  const current_user_groups = ref(localStorage.getItem("mapped_groups") ? JSON.parse(localStorage.getItem("mapped_groups")) : [])
  const isImpersonating = computed(() => {
    return !!(current_user.value && current_user.value.switched_by)
  });
  const debug_mode = ref(
    localStorage.getItem("debug_mode") !== null
      ? localStorage.getItem("debug_mode") === "true"
      : import.meta.env.VITE_DEBUG_MODE === "true"
  );
  watch(debug_mode, (val) => {
    localStorage.setItem("debug_mode", val)
  });
  // Recompute mapped groups when user or backend groups change
  watch([current_user, backend_groups], () => {
    if (current_user.value && backend_groups.value) {
      current_user_groups.value = current_user.value.groups.map(
        group_id => backend_groups.value[group_id] || `Unknown (${group_id})`
      );
      localStorage.setItem('mapped_groups', JSON.stringify(current_user_groups.value))
    } else {
      current_user_groups.value = [];
    }
  });

  const isLoggedIn = computed(() => !!accessToken.value)

  const refreshAccessToken = async () => {
    try {
      console.log("🔄 Attempting to refresh token...")
      const response = await apiClient.post("refresh_token/", { refresh: refreshToken.value })
      accessToken.value = response.data.access;
      localStorage.setItem("accessToken", response.data.access)
      setAuthToken(response.data.access);
      console.log("✅ Token refreshed successfully!")
      return true;
    } catch (error) {
      console.error("❌ Token refresh failed:", error)
      removeToken();
      return false;
    }
  };

  const setToken = async (token, refresh = null) => {
    accessToken.value = token;
    if (refresh) refreshToken.value = refresh
    localStorage.setItem("accessToken", token)
    if (refresh) localStorage.setItem("refreshToken", refresh)
    setAuthToken(token);

    try {
      const response = await apiClient.get("user_info/")
      current_user.value = response.data
      localStorage.setItem("current_user", JSON.stringify(response.data))
    } catch (error) {
      console.error("❌ Error loading user info:", error)
    }

    try {
      const response = await apiClient.get("groups_info/")
      backend_groups.value = response.data;
      localStorage.setItem("backend_groups", JSON.stringify(response.data))
    } catch (error) {
      console.error("❌ Error loading backend groups:", error)
    }
  };

  const removeToken = () => {
    console.log("🔴 Logging out...")
    accessToken.value = null
    refreshToken.value = null
    current_user.value = null
    backend_groups.value = {}
    current_user_groups.value = []
    localStorage.removeItem("accessToken")
    localStorage.removeItem("refreshToken")
    localStorage.removeItem("current_user")
    localStorage.removeItem("backend_groups")
    localStorage.removeItem("mapped_groups")
    localStorage.removeItem("debug_mode")

    debug_mode.value = import.meta.env.VITE_DEBUG_MODE === "true"

    setAuthToken(null)
  };

  const fetchBackendGroups = async () => {
    try {
      const response = await apiClient.get('groups_info/')
      backend_groups.value = response.data
      localStorage.setItem("backend_groups", JSON.stringify(response.data))

      if (current_user.value && backend_groups.value) {
        current_user_groups.value = current_user.value.groups.map(
          group_id => backend_groups.value[group_id] || `Unknown (${group_id})`
        )

        localStorage.setItem('mapped_groups', JSON.stringify(current_user_groups.value))
      }
    } catch (error) {
      console.error("Error fetching backend groups :", error)
    }
  }

  const createRole = async ( name ) => {
    try {
      await apiClient.post('create_group/', { name })
      await fetchBackendGroups()
    } catch (error) {
      throw error
    }
  }

  const fetchCurrentUserGroups = async () => {
    if (!current_user.value) return
    try {
      const response = await apiClient.get('get_user_roles/', { params: { userId: current_user.value.id } })
      current_user_groups.value = response.data.data
      localStorage.setItem('mapped_groups', JSON.stringify(current_user_groups.value))
    } catch (error) {
      console.error("Error fetching user roles :", error)
    }
  }

  return {
    isLoggedIn,
    accessToken,
    refreshToken,
    current_user,
    current_user_groups,
    backend_groups,
    setToken,
    refreshAccessToken,
    removeToken,
    isImpersonating,
    debug_mode,
    fetchBackendGroups,
    fetchCurrentUserGroups,
    createRole
  };
});
